<template>
  <div :class="!signedIn ? 'sticky-top' : undefined">
    <nav id="main-navigation">
      <div class="py-0 flex-nowrap navbar">
        <a :href="isLogged ? '/dashboard' : '/'">
          <img alt="arztpool.de" height="36" src="/images/arztpool.svg?url" />
        </a>
        <template v-if="!signedIn">
          <div v-if="!smAndDown" class="links">
            <a href="https://arztpool.de/praxen-mvz">{{ $t('NAVIGATION.MENU.LINKS.PRACTICES') }}</a>
            <a href="https://arztpool.de/vertreter">{{ $t('NAVIGATION.MENU.LINKS.REPRESENTATIVES') }}</a>
            <a href="https://arztpool.de/ueber-uns">{{ $t('NAVIGATION.MENU.LINKS.ABOUT_US') }}</a>
            <a href="https://arztpool.de/faq">{{ $t('NAVIGATION.MENU.LINKS.FAQ') }}</a>
          </div>
          <div class="buttons">
            <button @click="showRegistration">{{ $t('NAVIGATION.MENU.REGISTER') }}</button>
            <router-link v-if="!isLoginPage" to="/">{{ $t('NAVIGATION.MENU.LOGIN') }}</router-link>
          </div>
          <MyDoctorPool v-show="myDoctorPoolActive" @showLogin="showLogin" />
        </template>
        <template v-else>
          <div v-if="!hideNavigation" class="navigation" @mouseleave="hideAllMenus">
            <div v-if="!isOperator">
              <v-list>
                <v-list-item>
                  <router-link to="/dashboard">
                    {{ $t('NAVIGATION.MENU.LINKS.DASHBOARD') }}
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link to="/services" :class="{ 'router-link-active': $route.path === '/service' }">
                    {{ $t('NAVIGATION.MENU.LINKS.SERVICES') }}
                  </router-link>
                </v-list-item>
                <v-list-item>
                  <router-link to="/documents">
                    {{ $t('NAVIGATION.MENU.LINKS.DOCUMENTS') }}
                  </router-link>
                </v-list-item>
                <v-list-item class="item-icon">
                  <router-link to="/messages" class="text-none" stacked>
                    <v-badge v-if="unconfirmedMessages.length > 0 && !messagesLoading" :content="unconfirmedMessages.length">
                      <v-icon icon="far fa-bell" size="small" />
                    </v-badge>
                    <span v-else-if="unconfirmedMessages.length === 0 || messagesLoading">
                      <v-icon icon="far fa-bell" size="small" />
                    </span>
                  </router-link>
                </v-list-item>
              </v-list>
            </div>
            <div v-else>
              <div class="menu-general" @mouseover="showPublicMenu" @click.stop="togglePublicMenu">
                {{ $t('NAVIGATION.MENU.TITLE') }}
                <v-icon icon="fal fa-bars" />
              </div>
            </div>
            <div
              :class="{ 'signed-in': signedIn, active: myDoctorPoolActive }"
              class="btn-my-doctor-pool d-none d-md-block"
              @click="dashboardRedirect"
              @mouseover="showMyDoctorPoolMenu"
            >
              {{ $t('NAVIGATION.MY_DOCTOR_POOL.TITLE') }}
              <v-icon icon="fal fa-user-md" />
            </div>
            <MyDoctorPool v-show="myDoctorPoolActive" @showLogin="showLogin" />
            <PublicMenu v-show="publicMenuActive" @showLogin="showLogin" />
          </div>
        </template>
      </div>
    </nav>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, watch } from 'vue';
  import { useDisplay } from 'vuetify';
  import { useRoute } from 'vue-router';
  import { RouterSamePageErrorHandler } from '@/plugins/router';

  import Stores from '@/stores';

  import PublicMenu from '@/components/page/navigation/PublicMenu.vue';
  import MyDoctorPool from '@/components/page/navigation/MyDoctorPool.vue';
  import EventBus from '@/services/EventBus';

  export default defineComponent({
    name: 'PageNavigation',
    components: {
      PublicMenu,
      MyDoctorPool,
    },
    setup() {
      const { signedIn, userType } = Stores.userDataStoreToRefs();
      const { isOperator } = Stores.profileDataStoreToRefs();
      const { unconfirmedMessages } = Stores.messageStoreToRefs();

      const { smAndDown } = useDisplay();
      const route = useRoute();

      // register this const as ref to make it changeable in watcher
      const hideNavigation = ref(false);

      // watch for route changes to hide navigation on specific pages
      watch(
        () => route.path,
        (route) => {
          hideNavigation.value = route === '/wizard';
        },
        {
          immediate: true,
        },
      );

      return {
        signedIn,
        userType,
        isOperator,
        smAndDown,
        hideNavigation,
        unconfirmedMessages,
        publicMenuActive: ref(false),
        myDoctorPoolActive: ref(false),
      };
    },
    mounted() {
      if (this.$route.query.login === 'true') {
        this.showLogin();
      }
    },
    computed: {
      messagesLoading(): boolean {
        return Stores.message.messagesLoading;
      },
      isLogged(): boolean {
        return this.signedIn;
      },
      isLoginPage(): boolean {
        return this.$route.path === '/';
      },
    },
    methods: {
      /**
       * toggle display of public menu
       * @return {void}
       */
      togglePublicMenu(): void {
        this.publicMenuActive = !this.publicMenuActive;
      },

      /**
       * display public menu
       * @return {void}
       */
      showPublicMenu(): void {
        this.publicMenuActive = true;
        this.myDoctorPoolActive = false;
      },

      /**
       * hide display of public menu
       * @return {void}
       */
      hidePublicMenu(): void {
        this.publicMenuActive = false;
      },

      /**
       * display myDoctorPool menu
       * @return {void}
       */
      showMyDoctorPoolMenu(): void {
        this.publicMenuActive = false;
        this.myDoctorPoolActive = true;
      },

      /**
       * hide public and myDoctorPool menus
       * @return {void}
       */
      hideAllMenus(): void {
        this.publicMenuActive = false;
        this.myDoctorPoolActive = false;
      },

      /**
       * open login in modal
       * @return {void}
       */
      showLogin(): void {
        this.$router.push('/');
      },

      /**
       * open registration modal
       * @return {void}
       */
      showRegistration(): void {
        EventBus.emit(EventBus.keys.PREREGISTER_OPEN, true);
      },

      /**
       * redirect user to profile based on its user info
       * @return {void}
       */
      async dashboardRedirect(): Promise<void> {
        if (this.signedIn) {
          if (this.isOperator) {
            this.$router.push({ name: 'Operators' }).catch(RouterSamePageErrorHandler);
          } else {
            this.$router.push({ name: 'Dashboard' }).catch(RouterSamePageErrorHandler);
          }
        } else {
          return;
        }
      },
    },
  });
</script>

<style lang="scss" scoped>
  @import '@/scss/configs/colors';
  .sticky-top {
    position: sticky;
    top: 0;
    z-index: 1000;
  }
  #main-navigation:deep(.v-list-item__content) {
    overflow: visible;
  }
  #main-navigation:deep(.v-badge__badge) {
    border-color: $color-orange !important;
    background-color: $color-orange !important;
  }

  #main-navigation {
    min-width: 410px;
    position: relative;
    background-color: $color-white;
    z-index: 94;
    box-shadow: 0 2px 4px 0 rgba($color-black, 0.1);
    height: 5.9rem;

    .navigation-row {
      position: relative;
    }

    .navigation {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      padding-block-start: 1rem;
      padding-block-end: 1rem;
      font-size: 16px;
      color: $color-marine;
      height: 100%;

      &.sm {
        .svg-inline--fa {
          font-size: 20px;
          margin-right: 10px;
          color: $color-marine;
        }

        a.router-link-active {
          border: none;
        }
      }

      .v-list {
        display: inline-flex;

        .item-icon {
          padding: 0 0 0 12px;
          width: 65px;

          a {
            border-radius: 20px;
            height: 40px;
            width: 40px;
            display: flex;
            justify-content: center;
            align-items: center;
            .v-icon {
              color: $color-marine;
            }

            &.router-link-active {
              border-bottom-color: transparent;
              width: 40px;
              display: flex;
              justify-content: center;
              align-items: center;
              background-color: rgba($color-orange, 0.15) !important;
            }
          }
        }

        a {
          font-size: 20px;
          font-weight: bolder;
          color: $color-marine !important;
          text-decoration: none;
          border-bottom: 2px solid transparent;

          &.router-link-active {
            border-bottom-color: $color-orange;
            width: fit-content;
            display: inline-block;
          }
        }
      }

      .menu-general,
      .btn-my-doctor-pool {
        display: flex;
        align-items: center;
        user-select: none;

        svg {
          margin-left: 16px;
          font-size: 24px;
        }

        &:hover {
          cursor: pointer;
        }
      }

      .menu-general {
        font-weight: 600;
      }

      .btn-my-doctor-pool {
        border: 1px solid $color-marine;
        border-radius: 4px;
        padding: 12px 32px;
        background-color: $color-white;
        color: $color-marine;
        margin-left: 60px;

        &.signed-in {
          border-color: transparent;
          background-color: $color-marine;
          color: $color-white;
        }

        &:hover,
        &.active {
          border-color: transparent;
          background-color: $color-blue-grey-30;
          color: $color-marine;
        }
      }
    }
  }

  .navbar {
    height: 100%;
    margin-inline: 5rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .links {
    display: flex;
    flex-wrap: wrap;
    gap: 2rem;
    a {
      color: $color-marine;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .buttons {
    display: flex;
    gap: 1rem;

    a,
    button {
      color: $color-white;
      font-size: 0.875rem;
      text-transform: none;
      background-color: $color-marine;
      border-radius: 0.25rem;
      padding-block: 0.55rem;
      padding-inline: 2rem;

      &:hover {
        color: $color-marine-dark;
        background-color: $color-orange;
      }
    }
  }
</style>
